import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import API from '../../API';
import { useOverlay } from '../../contexts/FocusContext';
import { getFullscreenPortalTarget } from './fullscreenPortal';

type CallRoom = { id: string; url: string; version: number };
type Config = { rooms: CallRoom[]; can_edit: boolean };
export function callRoomInteractionId(id: unknown): string | undefined {
  return typeof id === 'string' ? /^call-room-enter-([1-3])$/.exec(id)?.[1] : undefined;
}
export function validMeetLink(value: string): boolean {
  try {
    const raw = value.trim(); const url = new URL(raw);
    return /^https:\/\/meet\.google\.com\//i.test(raw) && !raw.includes('\\') && raw.length <= 2048 && !/[\s\u0000-\u001f\u007f]/.test(raw) && url.protocol === 'https:' &&
      url.host.toLowerCase() === 'meet.google.com' && !url.username && !url.password && url.pathname !== '/';
  } catch { return false; }
}

/** One launcher for both the upstairs Unity interactables and the browser room menu. */
export default function CallRoomAccess({ accessToken, eventId }: { accessToken: string; eventId: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [room, setRoom] = useState<CallRoom | null>(null);
  const [draft, setDraft] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const capability = useRef<boolean | null>(null);
  const sequence = useRef(0);
  const pendingTab = useRef<Window | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const lastInteraction = useRef(0);
  const savingRef = useRef(false);
  useOverlay('call-room-link', selected !== null);

  const load = useCallback(async (): Promise<Config> => {
    const response = await API.getCallRooms(accessToken, { eventId });
    if (!response.ok) throw new Error('Could not load the Call Room links. Please try again.');
    const data = await response.json();
    if (!Array.isArray(data.rooms) || typeof data.can_edit !== 'boolean') throw new Error('Invalid Call Room response.');
    return data;
  }, [accessToken, eventId]);

  useEffect(() => {
    let cancelled = false; capability.current = null;
    setSelected(null); setRoom(null); setSaving(false); savingRef.current = false;
    void load().then(data => { if (!cancelled) capability.current = data.can_edit; }).catch(() => {});
    return () => { cancelled = true; ++sequence.current; pendingTab.current?.close(); pendingTab.current = null; };
  }, [load]);

  const close = () => {
    if (saving) return;
    ++sequence.current; pendingTab.current?.close(); pendingTab.current = null;
    setSelected(null); previousFocus.current?.focus();
  };

  const open = useCallback(async (id: string) => {
    if (savingRef.current || !['1', '2', '3'].includes(id)) return;
    const ticket = ++sequence.current;
    pendingTab.current?.close(); pendingTab.current = null;
    previousFocus.current = document.activeElement as HTMLElement;
    setSelected(id); setLoading(true); setRoom(null); setDraft(''); setError(''); setSaved(false); setCanEdit(false);
    // Preserve one-click joining for known ordinary users. Reserve a blank tab during the user
    // gesture, then resolve the latest saved URL. Never pre-open a meeting for staff/unknown roles.
    if (capability.current === false) {
      try {
        pendingTab.current = window.open('about:blank', '_blank');
        if (pendingTab.current) pendingTab.current.opener = null;
      } catch { pendingTab.current = null; }
    }
    const tab = pendingTab.current;
    try {
      const config = await load();
      if (ticket !== sequence.current) { tab?.close(); return; }
      capability.current = config.can_edit;
      const current = config.rooms.find(item => item.id === id);
      if (!current || !validMeetLink(current.url) || !Number.isSafeInteger(current.version) || current.version < 0)
        throw new Error('This Call Room does not have a valid Google Meet link.');
      setRoom(current); setDraft(current.url); setCanEdit(config.can_edit);
      if (!config.can_edit && tab && !tab.closed) {
        tab.location.replace(current.url); pendingTab.current = null; setSelected(null);
      } else {
        tab?.close(); pendingTab.current = null;
      }
    } catch (e) {
      tab?.close();
      if (ticket === sequence.current) { pendingTab.current = null; setError(e instanceof Error ? e.message : 'Could not load Call Room.'); }
    } finally { if (ticket === sequence.current) setLoading(false); }
  }, [load]);

  useEffect(() => {
    const requested = (event: Event) => { void open(String((event as CustomEvent).detail)); };
    const interacted = (event: Event) => {
      try {
        const detail = (event as CustomEvent).detail;
        const id = callRoomInteractionId((typeof detail === 'string' ? JSON.parse(detail) : detail)?.id);
        if (!id || Date.now() - lastInteraction.current < 1500) return;
        lastInteraction.current = Date.now(); void open(id);
      } catch { /* Ignore unrelated/malformed Unity events. */ }
    };
    window.addEventListener('callRoomRequested', requested);
    window.addEventListener('CustomInteractable', interacted);
    return () => { window.removeEventListener('callRoomRequested', requested); window.removeEventListener('CustomInteractable', interacted); };
  }, [open]);

  useEffect(() => {
    if (selected) (dialog.current?.querySelector('input') || dialog.current?.querySelector('button'))?.focus();
  }, [selected, loading]);

  const save = async () => {
    if (!room || !canEdit || savingRef.current) return;
    const url = draft.trim();
    if (!validMeetLink(url)) { setError('Enter an HTTPS Google Meet link, such as https://meet.google.com/abc-defg-hij.'); return; }
    const ticket = sequence.current;
    savingRef.current = true; setSaving(true); setError(''); setSaved(false);
    try {
      const response = await API.putCallRoom(accessToken, { eventId, callRoomId: room.id, url, version: room.version });
      if (!response.ok) throw new Error((await response.text()).trim() || 'Could not save this link.');
      const updated = await response.json();
      if (ticket === sequence.current) { setRoom(updated); setDraft(updated.url); setSaved(true); }
    } catch (e) { if (ticket === sequence.current) setError(e instanceof Error ? e.message : 'Could not save this link.'); }
    finally { if (ticket === sequence.current) { savingRef.current = false; setSaving(false); } }
  };

  if (!selected) return null;
  const dirty = !!room && draft.trim() !== room.url;
  return createPortal(<div className="unity-modal-overlay fixed inset-0 z-[12000] flex items-center justify-center bg-black/70 p-4" onClick={close}>
    <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="call-room-title" className="w-full max-w-lg rounded-2xl border border-cyan-500/40 bg-gray-900 p-6 text-white shadow-2xl" onClick={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); close(); }
        if (event.key === 'Tab') {
          const nodes = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),a[href]');
          if (!nodes?.length) return;
          const first = nodes[0], last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      }}>
      <div className="mb-4 flex items-center justify-between gap-4"><h2 id="call-room-title" className="text-xl font-bold">Call Room {selected}</h2><button type="button" aria-label="Close Call Room" disabled={saving} onClick={close}>✕</button></div>
      {loading ? <p role="status">Loading the current meeting link…</p> : room && <>
        {canEdit ? <><p className="mb-4 text-sm text-gray-300">Edit this room’s Google Meet link before joining. Saved changes apply to everyone.</p>
          <label htmlFor="call-room-url" className="text-sm font-semibold">Google Meet link</label>
          <input id="call-room-url" type="url" value={draft} maxLength={2048} disabled={saving} onChange={event => { setDraft(event.target.value); setSaved(false); }} className="mt-2 w-full rounded-lg border border-gray-600 bg-gray-800 p-3 text-white" />
        </> : <p className="break-all text-sm text-gray-300">{room.url}</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          {canEdit && <button type="button" disabled={saving || !dirty} onClick={() => void save()} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save link'}</button>}
          {!dirty && !saving ? <a href={room.url} target="_blank" rel="noopener noreferrer" onClick={close} className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold">Join Google Meet ↗</a> : <button disabled className="rounded-lg bg-gray-700 px-4 py-2 opacity-50">Save before joining</button>}
        </div>
      </>}
      {saved && <p role="status" className="mt-3 text-sm text-green-300">Link saved for everyone.</p>}
      {error && <div className="mt-3"><p role="alert" className="text-sm text-red-300">{error}</p><button type="button" disabled={saving} onClick={() => void open(selected)} className="mt-2 text-sm text-cyan-300 underline">Reload saved link</button></div>}
    </div>
  </div>, getFullscreenPortalTarget());
}
